import { useCallback, useMemo } from 'react'

import { useDefaultTradeName } from '../../../hooks/useDefaultTradeName'
import { useZervantDocumentTemplateSettings } from '../../../hooks/useZervantDocumentTemplateSettings'
import type { LogoAndBrandingSettingsFormValues } from '../types/logoAndBrandingSettingsFormValues'
import { getLogoAndBrandingTemplateFormValues } from '../utils/getLogoAndBrandingTemplateFormValues'
import { useZervantInvoiceLogoUrl } from './useZervantInvoiceLogoUrl'

export const usePredefinedLogoAndBrandingSettingsFormValues = () => {
  const {
    defaultTradeName,
    hasFailedToLoad: hasSettingsFailedToLoad,
    isLoading: isLoadingDefaultTradeName,
    refetch: refetchSettings,
  } = useDefaultTradeName()
  const { logoUrl, isLoading: isLoadingLogoUrl } = useZervantInvoiceLogoUrl()

  const {
    data: documentTemplateSettings,
    dataUpdatedAt: templateDataUpdatedAt,
    isError: isTemplateError,
    isLoading: isLoadingZervantDocumentTemplateSettings,
    refetch: refetchTemplate,
  } = useZervantDocumentTemplateSettings()

  const isLoading = isLoadingZervantDocumentTemplateSettings || isLoadingDefaultTradeName || isLoadingLogoUrl
  // A failed refetch keeps the last template, so only an error before the first successful load counts.
  const hasTemplateFailedToLoad = isTemplateError && !templateDataUpdatedAt
  // A missing logo is a 404, resolved to an empty logo. A template response with no settings
  // uses the form defaults.
  const hasFailedToLoad = !isLoading && (hasSettingsFailedToLoad || hasTemplateFailedToLoad)

  const refetch = useCallback(
    () => Promise.all([refetchSettings(), refetchTemplate()]),
    [refetchSettings, refetchTemplate],
  )

  const predefinedLogoAndBrandingSettingsFormValues: Partial<LogoAndBrandingSettingsFormValues> = useMemo(() => {
    const defaultLogoId = defaultTradeName?.logo?.id
    const defaultLogoUrl = defaultLogoId ? logoUrl : undefined

    return {
      logoId: defaultLogoId,
      logoUrl: defaultLogoUrl,
      ...getLogoAndBrandingTemplateFormValues(documentTemplateSettings),
    }
  }, [defaultTradeName, documentTemplateSettings, logoUrl])

  return { hasFailedToLoad, isLoading, predefinedLogoAndBrandingSettingsFormValues, refetch }
}
